import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  Fab,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {useCallback, useEffect, useRef, useState} from "react";
import type {CopilotInsightCard, CopilotQuickAction} from "../../ai/callable-ai";
import {isInsufficientAiCreditsError} from "../../ai/ai-credit-guard";
import {getSymplouGenerativeModel} from "../../ai/firebase-ai";
import {productRepository} from "../../product/product.repository";
import type {Sell} from "../sell.entity";
import {computeInsights, computeQuickActions, extractCartSummary} from "../utils/sell-copilot-engine";

export const COPILOT_PANEL_WIDTH = 340;

// ─── Design tokens ────────────────────────────────────────────────────────────
const P = {
  bg: "#07111f",
  headerFrom: "#0f172a",
  headerTo: "#09322e",
  teal: "#0d9488",
  tealLight: "#2dd4bf",
  glass: "rgba(255,255,255,0.05)",
  glassBorder: "rgba(255,255,255,0.10)",
  textPrimary: "#f8fafc",
  textSecondary: "rgba(248,250,252,0.55)",
  divider: "rgba(255,255,255,0.07)",
} as const;

// ─── SVG Decorations ──────────────────────────────────────────────────────────

const AiOrbitIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
    <defs>
      <radialGradient id="aio-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#0d9488" stopOpacity="0.6" />
      </radialGradient>
      <linearGradient id="aio-ring" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#2dd4bf" />
        <stop offset="100%" stopColor="#0d9488" />
      </linearGradient>
    </defs>
    {/* Outer orbit */}
    <ellipse cx="20" cy="20" rx="18" ry="8" stroke="url(#aio-ring)" strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
    <ellipse cx="20" cy="20" rx="8" ry="18" stroke="url(#aio-ring)" strokeWidth="1" strokeDasharray="4 3" opacity="0.3" />
    {/* Core */}
    <circle cx="20" cy="20" r="5.5" fill="url(#aio-glow)" />
    <circle cx="20" cy="20" r="2.5" fill="#f8fafc" opacity="0.9" />
    {/* Orbiting dot */}
    <circle cx="20" cy="2" r="2.5" fill="#2dd4bf" opacity="0.85" />
  </svg>
);

const NeuralHeaderBg = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 340 140"
    fill="none"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden
    style={{position: "absolute", inset: 0, pointerEvents: "none"}}
  >
    <defs>
      <radialGradient id="nh-orb1" cx="10%" cy="60%" r="50%">
        <stop offset="0%" stopColor="#0d9488" stopOpacity="0.18" />
        <stop offset="100%" stopColor="#0d9488" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="nh-orb2" cx="90%" cy="20%" r="50%">
        <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.12" />
        <stop offset="100%" stopColor="#2dd4bf" stopOpacity="0" />
      </radialGradient>
    </defs>
    <rect width="340" height="140" fill="url(#nh-orb1)" />
    <rect width="340" height="140" fill="url(#nh-orb2)" />
    {([
      [30, 110], [95, 80], [160, 105], [220, 70], [290, 95], [330, 60],
      [60, 45], [140, 30], [260, 40],
    ] as [number, number][]).map(([cx, cy], i) => (
      <circle key={i} cx={cx} cy={cy} r={i % 3 === 0 ? 3 : 2} fill="#2dd4bf" opacity={0.18 + (i % 3) * 0.06} />
    ))}
    {([
      [[30, 110], [95, 80]], [[95, 80], [160, 105]], [[160, 105], [220, 70]],
      [[220, 70], [290, 95]], [[290, 95], [330, 60]], [[60, 45], [140, 30]],
      [[140, 30], [260, 40]], [[95, 80], [60, 45]], [[220, 70], [260, 40]],
    ] as [[number, number], [number, number]][]).map(([[x1, y1], [x2, y2]], i) => (
      <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#2dd4bf" strokeWidth="0.7" opacity="0.14" />
    ))}
  </svg>
);

const WaveDivider = () => (
  <svg width="100%" height="12" viewBox="0 0 340 12" fill="none" aria-hidden style={{display: "block"}}>
    <path
      d="M0 6 Q42.5 0 85 6 Q127.5 12 170 6 Q212.5 0 255 6 Q297.5 12 340 6"
      stroke="rgba(255,255,255,0.08)"
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
);

const EmptyIllustration = ({label}: {label: string}) => (
  <Box sx={{textAlign: "center", py: 2}}>
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden style={{margin: "0 auto", display: "block"}}>
      <defs>
        <radialGradient id="ei-bg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#0d9488" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#0d9488" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="32" cy="32" r="28" fill="url(#ei-bg)" />
      <rect x="18" y="30" width="28" height="22" rx="4" stroke="#2dd4bf" strokeWidth="1.5" strokeOpacity="0.4" fill="none" />
      <path d="M24 30 Q24 20 32 20 Q40 20 40 30" stroke="#2dd4bf" strokeWidth="1.5" fill="none" strokeOpacity="0.4" strokeLinecap="round" />
      <path d="M29 41 L35 41 M32 38 L32 44" stroke="#2dd4bf" strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.5" />
      <path d="M32 10 L33.2 13 L36 13 L33.8 14.8 L34.6 17.5 L32 16 L29.4 17.5 L30.2 14.8 L28 13 L30.8 13 Z" fill="#2dd4bf" opacity="0.5" />
    </svg>
    <Typography sx={{color: P.textSecondary, fontSize: 12, mt: 1}}>{label}</Typography>
  </Box>
);

const PulseDot = () => (
  <Box sx={{position: "relative", width: 8, height: 8, flexShrink: 0}}>
    <Box sx={{
      width: 8, height: 8, borderRadius: "50%", bgcolor: "#4ade80",
      position: "relative", zIndex: 1,
    }} />
    <Box sx={{
      position: "absolute", inset: -3, borderRadius: "50%",
      bgcolor: "#4ade80", opacity: 0.35,
      "@keyframes copilot-pulse": {
        "0%": {transform: "scale(0.85)", opacity: 0.4},
        "100%": {transform: "scale(2.4)", opacity: 0},
      },
      animation: "copilot-pulse 1.8s ease-out infinite",
    }} />
  </Box>
);

type InsightCardProps = {
  card: CopilotInsightCard;
  onAdd: () => void;
};
const InsightCard = ({card, onAdd}: InsightCardProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      bgcolor: P.glass,
      border: `1px solid ${P.glassBorder}`,
      borderLeft: `3px solid ${P.teal}`,
      borderRadius: "10px",
      px: 1.5,
      py: 1,
      mb: 1,
      transition: "all .15s",
      "&:hover": {bgcolor: "rgba(255,255,255,0.08)", borderLeftColor: P.tealLight},
    }}
  >
    <Box sx={{flex: 1, minWidth: 0}}>
      <Typography sx={{color: P.textPrimary, fontWeight: 600, fontSize: 13, lineHeight: 1.3}} noWrap>
        {card.title}
      </Typography>
      <Typography sx={{color: P.textSecondary, fontSize: 11, lineHeight: 1.4}} noWrap>
        {card.subtitle}
      </Typography>
    </Box>
    <Button
      size="small"
      onClick={onAdd}
      sx={{
        flexShrink: 0,
        minWidth: 0,
        fontSize: 11,
        fontWeight: 700,
        color: P.tealLight,
        border: `1px solid rgba(45,212,191,0.35)`,
        borderRadius: "7px",
        px: 1.2,
        py: 0.3,
        lineHeight: 1.6,
        "&:hover": {bgcolor: "rgba(45,212,191,0.12)", borderColor: P.tealLight},
      }}
    >
      + Add
    </Button>
  </Box>
);

type QuickActionBtnProps = {
  action: CopilotQuickAction;
  onClick: () => void;
};
const QuickActionBtn = ({action, onClick}: QuickActionBtnProps) => (
  <Button
    fullWidth
    size="small"
    onClick={onClick}
    sx={{
      justifyContent: "flex-start",
      color: P.textPrimary,
      bgcolor: P.glass,
      border: `1px solid ${P.glassBorder}`,
      borderRadius: "9px",
      px: 1.5,
      py: 0.8,
      fontSize: 12,
      fontWeight: 600,
      mb: 0.75,
      textAlign: "left",
      "&:hover": {bgcolor: "rgba(13,148,136,0.15)", borderColor: `rgba(45,212,191,0.4)`},
    }}
  >
    <Box
      component="span"
      sx={{
        width: 6, height: 6, borderRadius: "50%",
        bgcolor: P.tealLight, mr: 1.25, flexShrink: 0,
      }}
    />
    {action.title}
    {action.confirm && (
      <Box component="span" sx={{ml: "auto", fontSize: 10, color: P.textSecondary, fontWeight: 400}}>
        confirmar
      </Box>
    )}
  </Button>
);

// ─── AI subtitle enrichment via Firebase AI Logic (client-side Gemini) ────────

async function enrichSubtitlesWithAi(
  insights: CopilotInsightCard[],
  cartNames: string[]
): Promise<CopilotInsightCard[]> {
  if (insights.length === 0 || cartNames.length === 0) return insights;
  try {
    const model = getSymplouGenerativeModel();
    const cartStr = cartNames.slice(0, 5).join(", ");
    const itemList = insights.map((ins, i) => `${i + 1}. ${ins.title}`).join("\n");
    const prompt = `PDV brasileiro. Carrinho: ${cartStr}.\nSugestões:\n${itemList}\nPara cada item escreva motivo curto pt-BR (máx 28 chars, informal). JSON apenas: {"r":["m1","m2","m3"]}`;
    const result = await model.generateContent(prompt);
    const text = result.response.text().trim();
    const match = /\{[\s\S]*?\}/.exec(text);
    if (!match) return insights;
    const parsed = JSON.parse(match[0]) as {r?: unknown};
    if (!Array.isArray(parsed.r)) return insights;
    const reasons = parsed.r as unknown[];
    return insights.map((ins, i) => ({
      ...ins,
      subtitle: typeof reasons[i] === "string"
        ? (reasons[i] as string).slice(0, 35)
        : ins.subtitle,
    }));
  } catch {
    return insights;
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

type SellCopilotPanelProps = {
  sell: Sell | null;
  onAddProductById: (productId: string) => Promise<void>;
  onApplyDiscount?: (percent: number) => void;
  onMarkAsCredit?: () => void;
  onRemoveLastItem?: () => void;
  onClearCart?: () => void;
  onDuplicateLastItem?: () => void;
  onRoundTotal?: (target: number) => void;
};

// ─── Main component ───────────────────────────────────────────────────────────

export const SellCopilotPanel = ({
  sell,
  onAddProductById,
  onApplyDiscount,
  onMarkAsCredit,
  onRemoveLastItem,
  onClearCart,
  onDuplicateLastItem,
  onRoundTotal,
}: SellCopilotPanelProps) => {
  const [insights, setInsights] = useState<CopilotInsightCard[]>([]);
  const [quickActions, setQuickActions] = useState<CopilotQuickAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<CopilotQuickAction | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Track last cart snapshot to detect meaningful changes (debounced)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCartKeyRef = useRef<string>("");

  const runEngine = useCallback(async (force = false) => {
    if (!sell) return;

    const cart = extractCartSummary(sell);

    // Fingerprint the cart to avoid redundant re-runs
    const cartKey = cart.items.map((i) => `${i.productId}:${i.quantity}`).sort().join("|");
    if (!force && cartKey === lastCartKeyRef.current) return;
    lastCartKeyRef.current = cartKey;

    // --- Step 1: Instant algorithmic results (no network, no AI) ---
    if (cart.items.length === 0) {
      setInsights([]);
      setQuickActions([]);
      return;
    }

    setLoading(true);
    setError(null);

    let catalog: Awaited<ReturnType<typeof productRepository.getCatalogForCopilot>>;
    try {
      catalog = await productRepository.getCatalogForCopilot();
    } catch {
      setError("Não foi possível carregar os produtos.");
      setLoading(false);
      return;
    }

    const rawInsights = computeInsights(cart, catalog);
    const actions = computeQuickActions(cart);

    // Show algorithmic results immediately — user sees something instantly
    setInsights(rawInsights);
    setQuickActions(actions);
    setLoading(false);

    // --- Step 2: Enrich subtitles with Firebase AI Logic (client-side, no credit cost) ---
    if (rawInsights.length > 0) {
      const cartNames = cart.items.map((i) => i.name);
      const enriched = await enrichSubtitlesWithAi(rawInsights, cartNames).catch(() => rawInsights);
      setInsights(enriched);
    }
  }, [sell]);

  // Debounce cart changes: wait 1.5 s after the last mutation before re-running
  const scheduleRun = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void runEngine(), 1500);
  }, [runEngine]);

  // Derive a stable fingerprint from the sell to trigger schedule
  const cartFingerprint = sell
    ? sell.getProducts().getValues().map((p) => `${p.entity.id}:${p.quantity}`).join("|")
    : "";

  useEffect(() => {
    scheduleRun();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartFingerprint]);

  const executePdvAction = useCallback((a: CopilotQuickAction) => {
    switch (a.pdvAction) {
      case "apply_discount": onApplyDiscount?.(a.discountPercent ?? 10); break;
      case "mark_as_credit": onMarkAsCredit?.(); break;
      case "remove_last_item": onRemoveLastItem?.(); break;
      case "clear_cart": onClearCart?.(); break;
      case "duplicate_last_item": onDuplicateLastItem?.(); break;
      case "round_total": if (a.roundTarget) onRoundTotal?.(a.roundTarget); break;
    }
    setPendingAction(null);
  }, [onApplyDiscount, onMarkAsCredit, onRemoveLastItem, onClearCart, onDuplicateLastItem, onRoundTotal]);

  const handleAction = useCallback((a: CopilotQuickAction) => {
    if (a.confirm) setPendingAction(a);
    else executePdvAction(a);
  }, [executePdvAction]);

  // ── Panel Content ────────────────────────────────────────────────────────────

  const panelContent = (
    <Box sx={{display: "flex", flexDirection: "column", height: "100%", bgcolor: P.bg, overflow: "hidden"}}>

      {/* Header */}
      <Box sx={{
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(145deg, ${P.headerFrom} 0%, ${P.headerTo} 100%)`,
        flexShrink: 0,
        borderBottom: `1px solid ${P.divider}`,
      }}>
        <NeuralHeaderBg />
        <Box sx={{position: "relative", zIndex: 1, px: 2.5, pt: 2.5, pb: 2}}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <AiOrbitIcon />
            <Box sx={{flex: 1}}>
              <Typography sx={{
                color: P.textPrimary, fontWeight: 800, fontSize: 15, lineHeight: 1.2,
                letterSpacing: "-0.01em",
              }}>
                Copiloto de Vendas
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{mt: 0.4}}>
                <PulseDot />
                <Typography sx={{color: "#4ade80", fontSize: 10, fontWeight: 600, letterSpacing: "0.04em"}}>
                  ONLINE
                </Typography>
              </Stack>
            </Box>
            <Tooltip title="Atualizar sugestões">
              <span>
                <IconButton
                  size="small"
                  onClick={() => void runEngine(true)}
                  disabled={loading}
                  sx={{color: P.textSecondary, "&:hover": {color: P.tealLight, bgcolor: "rgba(255,255,255,0.06)"}}}
                >
                  {loading
                    ? <CircularProgress size={14} sx={{color: P.tealLight}} />
                    : <RefreshIcon sx={{fontSize: 16}} />
                  }
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
        <WaveDivider />
      </Box>

      {/* Scrollable body */}
      <Box sx={{flex: 1, overflowY: "auto", px: 2, pt: 2, pb: 1,
        "&::-webkit-scrollbar": {width: 4},
        "&::-webkit-scrollbar-track": {background: "transparent"},
        "&::-webkit-scrollbar-thumb": {background: "rgba(255,255,255,0.12)", borderRadius: 4},
      }}>

        {/* Error */}
        {error ? (
          <Box sx={{
            bgcolor: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: "9px", px: 1.5, py: 1, mb: 1.5,
            display: "flex", alignItems: "center", gap: 1,
          }}>
            <Typography sx={{color: "#fca5a5", fontSize: 12, flex: 1}}>{error}</Typography>
            <IconButton size="small" onClick={() => setError(null)} sx={{color: "#fca5a5", p: 0.25}}>
              <CloseIcon sx={{fontSize: 14}} />
            </IconButton>
          </Box>
        ) : null}

        {/* Pending confirmation */}
        {pendingAction ? (
          <Box sx={{
            bgcolor: "rgba(234,179,8,0.12)", border: "1px solid rgba(234,179,8,0.35)",
            borderRadius: "9px", px: 1.5, py: 1, mb: 1.5,
          }}>
            <Typography sx={{color: "#fde047", fontSize: 12, fontWeight: 600, mb: 0.75}}>
              {pendingAction.title} — confirmar?
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => executePdvAction(pendingAction)} sx={{
                bgcolor: "rgba(234,179,8,0.2)", color: "#fde047",
                border: "1px solid rgba(234,179,8,0.4)", borderRadius: "7px",
                fontSize: 11, fontWeight: 700, px: 1.5, py: 0.3,
                "&:hover": {bgcolor: "rgba(234,179,8,0.3)"},
              }}>
                Confirmar
              </Button>
              <Button size="small" onClick={() => setPendingAction(null)} sx={{
                color: P.textSecondary, border: `1px solid ${P.glassBorder}`,
                borderRadius: "7px", fontSize: 11, px: 1.5, py: 0.3,
                "&:hover": {bgcolor: P.glass},
              }}>
                Cancelar
              </Button>
            </Stack>
          </Box>
        ) : null}

        {/* Insights section */}
        <Box sx={{mb: 2}}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1.25}}>
            <Box sx={{
              width: 18, height: 18, borderRadius: "50%",
              bgcolor: "rgba(45,212,191,0.15)", border: "1px solid rgba(45,212,191,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                <path d="M5 1L5.9 3.7H8.5L6.4 5.4L7.3 8.1L5 6.5L2.7 8.1L3.6 5.4L1.5 3.7H4.1Z" fill="#2dd4bf" />
              </svg>
            </Box>
            <Typography sx={{color: P.textSecondary, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em"}}>
              Sugestões
            </Typography>
            <Box sx={{flex: 1, height: "1px", bgcolor: P.divider}} />
          </Stack>

          {loading && insights.length === 0 ? (
            <Box sx={{display: "flex", alignItems: "center", gap: 1, py: 1.5}}>
              <CircularProgress size={14} sx={{color: P.teal}} />
              <Typography sx={{color: P.textSecondary, fontSize: 12}}>Analisando carrinho…</Typography>
            </Box>
          ) : insights.length === 0 ? (
            <EmptyIllustration label="Adicione produtos para ver sugestões" />
          ) : (
            insights.map((card, i) => (
              <InsightCard
                key={i}
                card={card}
                onAdd={() => void onAddProductById(card.productId)}
              />
            ))
          )}
        </Box>

        {/* Quick actions section */}
        {quickActions.length > 0 ? (
          <Box sx={{mb: 2}}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1.25}}>
              <Box sx={{
                width: 18, height: 18, borderRadius: "50%",
                bgcolor: "rgba(45,212,191,0.15)", border: "1px solid rgba(45,212,191,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                  <path d="M5.5 1L1 6H5L4.5 9L9 4H5L5.5 1Z" fill="#2dd4bf" />
                </svg>
              </Box>
              <Typography sx={{color: P.textSecondary, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em"}}>
                Ações rápidas
              </Typography>
              <Box sx={{flex: 1, height: "1px", bgcolor: P.divider}} />
            </Stack>
            {quickActions.map((a, i) => (
              <QuickActionBtn key={i} action={a} onClick={() => handleAction(a)} />
            ))}
          </Box>
        ) : null}

        <div />
      </Box>
    </Box>
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Fixed right panel — desktop (lg+) */}
      <Box sx={{
        display: {xs: "none", lg: "flex"},
        flexDirection: "column",
        position: "fixed",
        top: 64,
        right: 0,
        bottom: 0,
        width: COPILOT_PANEL_WIDTH,
        zIndex: (t) => t.zIndex.appBar - 1,
        borderLeft: `1px solid ${P.divider}`,
        boxShadow: "-8px 0 32px rgba(0,0,0,0.25)",
      }}>
        {panelContent}
      </Box>

      {/* FAB — mobile/tablet */}
      <Box sx={{
        display: {xs: "flex", lg: "none"},
        position: "fixed",
        bottom: 88,
        right: 20,
        zIndex: (t) => t.zIndex.appBar + 5,
      }}>
        <Fab
          size="medium"
          onClick={() => setMobileOpen(true)}
          sx={{
            background: `linear-gradient(135deg, ${P.teal} 0%, #0f766e 100%)`,
            color: "#fff",
            boxShadow: `0 6px 24px rgba(13,148,136,0.45)`,
            "&:hover": {boxShadow: `0 8px 28px rgba(13,148,136,0.55)`},
          }}
        >
          <AutoAwesomeIcon />
        </Fab>
      </Box>

      {/* Bottom drawer — mobile/tablet */}
      <Drawer
        anchor="bottom"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{display: {xs: "block", lg: "none"}}}
        PaperProps={{
          sx: {
            height: "78vh",
            borderRadius: "20px 20px 0 0",
            bgcolor: P.bg,
            backgroundImage: "none",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5}}>
          <Box sx={{width: 36, height: 4, borderRadius: 2, bgcolor: P.glassBorder}} />
        </Box>
        <Box sx={{flex: 1, overflow: "hidden", display: "flex", flexDirection: "column"}}>
          {panelContent}
        </Box>
      </Drawer>
    </>
  );
};
